// UI strings for Taivutusharjoitus, the word-bending practice between a lesson's chapters (app/src/bendpractice/,
// Axel 2026-10-22): the break's head, the show step (the word's forms so far, the chapter's lines) and the short
// test (pick the form, say it aloud). Style: docs/FINNISH-STYLE.md (sinä, no jargon, only actionable text).
import type { FormAskKind } from '../../exercises/formAsk';
import { defineArea } from '../area';

export const bendPracticeFi = {
  /** The break's kicker, above the words it practises. */
  kicker: 'Taivutusharjoitus',
  start: 'Harjoittele taivutusta',
  skip: 'Ohita',
  /** The pattern line: "Vartalo iderēb- · päätteet -an, -ā, -as" (two stems: "Vartalot urne- ja urn- …"). */
  stems: (n: number): string => (n > 1 ? 'Vartalot ' : 'Vartalo '),
  and: ' ja ',
  endings: (n: number): string => (n > 1 ? ' · päätteet ' : ' · pääte '),
  /** The word's whole table (opens and closes). */
  tableH: 'Koko taivutus',
  /** Above the table when the chapter used some of its forms (they are framed). */
  marked: 'Kehystetyt muodot ovat tästä luvusta.',
  linesH: 'Luvun esimerkit',
  toTest: 'Kokeile itse',
  /** The pick question around the word and the asked form: case and number "Mikä on vala monikossa?", else "Valitse sanan urnegon muoto: …". */
  pick: {
    number: ['Mikä on ', ' ', '?'],
    case: ['Mikä on ', ' ', '?'],
    imperative: ['Valitse sanan ', ' muoto: ', ''],
    finite: ['Valitse sanan ', ' muoto: ', ''],
    other: ['Valitse sanan ', ' muoto: ', ''],
  } as Record<FormAskKind, readonly [string, string, string]>,
  right: 'Oikein.',
  /** A wrong pick: the right form between the two parts. */
  wrong: ['Ei aivan. Oikea muoto on ', '. Valitse se.'] as readonly [string, string],
  /** Said once a missed form went to Omat virheet. */
  noted: 'Muoto lisättiin Omiin virheisiin.',
  say: 'Sano ääneen',
  /** No clip of Peterson's for the form: say it by its stress and pronunciation, record yourself to compare. */
  noClip: 'Petersonin äänitettä tästä muodosta ei ole. Sano se painon ja ääntämyksen mukaan ja vertaa omaan äänitykseesi.',
  sayDone: 'Sanoin sen',
  next: 'Seuraava',
  finish: 'Valmis',
  progress: (i: number, n: number) => `${i}/${n}`,
  /** A finished or skipped break, remembered (shown collapsed). */
  practised: 'Harjoiteltu',
  /** A verb card's line after the reveal: "perusmuoto · vartalo iderēb-" (bendpractice/VerbBase.tsx). */
  base: 'perusmuoto',
  again: 'Harjoittele uudelleen',
};

export const bendPracticeEn: typeof bendPracticeFi = {
  kicker: 'Inflection practice',
  start: 'Practise the forms',
  skip: 'Skip',
  stems: (n) => (n > 1 ? 'Stems ' : 'Stem '),
  and: ' and ',
  endings: (n: number) => (n > 1 ? ' · endings ' : ' · ending '),
  tableH: 'Whole table',
  marked: 'The framed forms are from this chapter.',
  linesH: 'From this chapter',
  toTest: 'Try it',
  pick: {
    number: ['What is ', ' in the ', '?'],
    case: ['What is ', ' in the ', '?'],
    imperative: ['Pick the form of ', ': ', ''],
    finite: ['Pick the form of ', ': ', ''],
    other: ['Pick the form of ', ': ', ''],
  },
  right: 'Right.',
  wrong: ['Not quite. The right form is ', '. Pick it.'],
  noted: 'The form was added to Your mistakes.',
  say: 'Say it aloud',
  noClip: 'Peterson has no recording of this form. Say it by its stress and pronunciation, and record yourself to compare.',
  sayDone: 'I said it',
  next: 'Next',
  finish: 'Done',
  progress: (i, n) => `${i}/${n}`,
  practised: 'Practised',
  base: 'dictionary form',
  again: 'Practise again',
};

// Messages.bendPractice (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('bendPractice', bendPracticeFi, bendPracticeEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof bendPracticeFi> {}
}
